import { Box, Container, Stack } from "@mantine/core";
import { AppRoutes } from "@/constants";
import { LegacyDeprecationBanner } from "@/components/FrontendMigration";
import { useFrontendMigrationUiStore } from "@/components/FrontendMigration/frontendMigrationStore";
import { shouldShowLegacyDeprecationBanner } from "@/utils/frontendMigration";
import LiteTopNavbar from "./components/components/LiteTopNavbar";
import SmartIntakeSettingsPage from "./components/components/SmartIntakeSettingsPage";
import OnboardingStepsGrid from "./components/components/OnboardingStepsGrid";
import TeamManagementOnboarding from "./components/components/TeamManagementOnboarding";
import OneClickOnboarding from "./components/components/OneClickOnboarding";
import ReferralOnboarding from "./components/components/ReferralOnboarding";
import IntegrationOnboarding from "./components/components/IntegrationOnboarding";
import ImportClientGuide from "./components/components/ImportClientGuide";
import OnboardingStepBanner from "./components/components/OnboardingStepBanner";
import MobileView from "./components/components/MobileView";
import { useLiteHomeInit } from "@/hooks/useLiteHomeInit.ts";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { useTaxOfficeStore } from "@pages/Onboarding/stores/taxOfficeStore";
import { fetchOnboardingSteps } from "./api/onboardingStepsApi";
import type {
  OnboardingStepId,
  OnboardingStepItem,
  IntegrationMetadata,
  ImportClientMetadata,
} from "./components/types";
import {
  executeStepAction,
  isExternalAction,
} from "./components/handlers/onboardingStepActions";
import { triggerSampleClientOnboarding } from "./components/handlers/sampleClientOnboarding";
import AccountSettingsPage from "./components/components/AccountSettingsPage";
import BusinessSettingsPage from "./components/components/BusinessSettingsPage";
import TeamSettingsPage from "./components/components/TeamSettingsPage";
import BillingSettingsPage from "./components/components/BillingSettingsPage";
import IntegrationSettingsPage from "./components/components/IntegrationSettingsPage";
import SettingsCardsGrid from "./components/components/SettingsCardsGrid";
import { useLiteHomeStore } from "./stores/useLiteHomeStore";
import { isMobileDevice } from "@/utils/deviceDetection";
import { useAuthStore } from "@utils/stores/authStore";

/** Migration card overlays page content in the bottom-left corner. */
const LITE_HOME_MIGRATION_OVERLAY_STYLE = {
  position: "fixed",
  bottom: 0,
  left: 0,
  zIndex: 200,
  width: "15.375rem",
  padding: "0 5px 16px",
  pointerEvents: "none",
} as const;

const SmartwizLiteHome = () => {
  useLiteHomeInit();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const taxOfficeData = useTaxOfficeStore((state) => state.data);
  const fetchTaxOfficeData = useTaxOfficeStore((state) => state.fetchData);

  // Navigation store
  const {
    currentView,
    showOnboardingGrid,
    showSettingsGrid,
    showOnboardingStep,
    goBack,
    syncFromUrl,
    getUrlParams,
  } = useLiteHomeStore();

  const hideBilling = useAuthStore((state) =>
    state.shouldHideBillingSettings()
  );
  const user = useAuthStore((state) => state.user);
  const migrationBannerDismissed = useFrontendMigrationUiStore(
    (state) => state.bannerDismissed
  );
  const showMigrationCard = Boolean(
    user && !migrationBannerDismissed && shouldShowLegacyDeprecationBanner(user)
  );

  // Onboarding steps state
  const [steps, setSteps] = useState<OnboardingStepItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingStepId, setLoadingStepId] = useState<string | null>(null);

  // Sync navigation state from URL
  useEffect(() => {
    const stepFromUrl = searchParams.get("step");
    const settingFromUrl = searchParams.get("setting");

    // External actions (like WIZZY_DO, INTAKE_DOCS) don't have pages
    // If URL contains an external action, redirect to onboarding grid
    if (stepFromUrl) {
      const upperStepId = stepFromUrl.toUpperCase() as OnboardingStepId;
      if (isExternalAction(upperStepId)) {
        // Clear URL and show onboarding grid
        navigate(AppRoutes.homeLite, { replace: true });
        showOnboardingGrid();
        return;
      }
    }

    if (settingFromUrl?.toLowerCase() === "billing" && hideBilling) {
      navigate(AppRoutes.homeLite, { replace: true });
      showSettingsGrid();
      return;
    }

    syncFromUrl(stepFromUrl, settingFromUrl);
  }, [
    searchParams,
    syncFromUrl,
    navigate,
    showOnboardingGrid,
    showSettingsGrid,
    hideBilling,
  ]);

  // Sync URL from navigation state changes
  useEffect(() => {
    const urlParams = getUrlParams();
    const currentStepParam = searchParams.get("step");
    const currentSettingParam = searchParams.get("setting");

    // Only update URL if the managed params (step/setting) are actually different
    // Use nullish coalescing to treat undefined and null as equivalent
    const needsUpdate =
      (urlParams.step ?? null) !== currentStepParam ||
      (urlParams.setting ?? null) !== currentSettingParam;

    if (needsUpdate) {
      // Preserve existing query params and only update step/setting
      const params = new URLSearchParams(searchParams);

      // Update or remove step param
      if (urlParams.step) {
        params.set("step", urlParams.step);
      } else {
        params.delete("step");
      }

      // Update or remove setting param
      if (urlParams.setting) {
        params.set("setting", urlParams.setting);
      } else {
        params.delete("setting");
      }

      const newUrl = params.toString()
        ? `${AppRoutes.homeLite}?${params.toString()}`
        : AppRoutes.homeLite;

      navigate(newUrl, { replace: true });
    }
  }, [currentView, navigate, searchParams, getUrlParams]);

  // Fetch tax office data to determine software type
  useEffect(() => {
    if (!taxOfficeData) {
      fetchTaxOfficeData();
    }
  }, [taxOfficeData, fetchTaxOfficeData]);

  // Fetch onboarding steps on mount
  const refreshSteps = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchOnboardingSteps();
      setSteps(response.steps);
    } catch (error) {
      console.error("Failed to load onboarding steps:", error);
      setError("Failed to load onboarding steps. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSteps();
  }, [refreshSteps]);

  // Refresh steps when page comes back into focus or window regains focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshSteps();
      }
    };

    const handleWindowFocus = () => {
      refreshSteps();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleWindowFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [refreshSteps]);

  // Wrapper for setActiveStep that matches the expected signature
  const setActiveStep = useCallback(
    (stepId: OnboardingStepId | null) => {
      if (stepId) {
        showOnboardingStep(stepId);
      } else {
        showOnboardingGrid();
      }
    },
    [showOnboardingStep, showOnboardingGrid]
  );

  // Handle step click action
  const handleStepClick = useCallback(
    async (stepId: string) => {
      console.log(`[handleStepClick] Called with stepId: ${stepId}`);
      setLoadingStepId(stepId);
      try {
        // Special handling for SAMPLE_CLIENT - trigger extension flow
        if (stepId === "SAMPLE_CLIENT") {
          console.log(
            "[handleStepClick] Triggering sample client onboarding..."
          );
          await triggerSampleClientOnboarding();
          return;
        }

        // For INTEGRATION and IMPORT_CLIENT steps, don't navigate to a URL
        // Just execute the action directly (opens extension)
        if (stepId === "INTEGRATION" || stepId === "IMPORT_CLIENT") {
          await executeStepAction(stepId as OnboardingStepId, {
            navigate,
            setActiveStep,
            refreshSteps,
            steps,
          });
        } else {
          // For other steps that need to show a page, show the step view
          showOnboardingStep(stepId as OnboardingStepId);

          await executeStepAction(stepId as OnboardingStepId, {
            navigate,
            setActiveStep,
            refreshSteps,
            steps,
          });
        }
      } catch (error) {
        console.error("Failed to execute step action:", error);
      } finally {
        setLoadingStepId(null);
      }
    },
    [navigate, refreshSteps, steps, setActiveStep, showOnboardingStep]
  );

  // Handle back navigation
  const handleBack = useCallback(() => {
    goBack();
    // Refresh steps when returning to grid
    if (currentView.type === "onboarding-step") {
      refreshSteps();
    }
  }, [goBack, currentView.type, refreshSteps]);

  // Handle toggle between onboarding steps and settings grid
  const handleToggleOnboardingSteps = useCallback(() => {
    if (currentView.type === "settings-grid") {
      showOnboardingGrid();
    } else {
      showSettingsGrid();
    }
  }, [currentView.type, showOnboardingGrid, showSettingsGrid]);

  // Handle background click to refresh steps
  const handleBackgroundClick = useCallback(
    (e: React.MouseEvent) => {
      // Only refresh if clicking the background itself, not child elements
      if (e.target === e.currentTarget) {
        refreshSteps();
      }
    },
    [refreshSteps]
  );

  // Determine if we should show the back banner
  const showBackBanner =
    currentView.type === "onboarding-step" ||
    currentView.type === "settings-page";

  // Determine if we should hide settings button
  const hideSettingsButton = currentView.type === "settings-page";

  // Show mobile view if user is on a mobile device
  // This check must be AFTER all hooks have been called
  const isMobile = isMobileDevice();
  if (isMobile) {
    return <MobileView />;
  }

  return (
    <>
      {showMigrationCard && (
        <Box style={LITE_HOME_MIGRATION_OVERLAY_STYLE}>
          <Box style={{ pointerEvents: "auto" }}>
            <LegacyDeprecationBanner />
          </Box>
        </Box>
      )}

      <Container size="fluid" p="md" style={{ maxWidth: 1200 }}>
        <LiteTopNavbar
          onSettingsClick={showSettingsGrid}
          onHomeClick={showOnboardingGrid}
          hideSettings={hideSettingsButton}
        />

        <Stack gap="xl" onClick={handleBackgroundClick}>
          {/* Back banner when viewing a specific step or settings page */}
          {showBackBanner && <OnboardingStepBanner onBack={handleBack} />}

          {/* Conditional rendering based on current view */}
          {currentView.type === "settings-page" && (
            <>
              {currentView.pageId === "ACCOUNT" && (
                <AccountSettingsPage onExit={handleBack} />
              )}
              {currentView.pageId === "BUSINESS" && (
                <BusinessSettingsPage onExit={handleBack} />
              )}
              {currentView.pageId === "TEAM" && (
                <TeamSettingsPage onExit={handleBack} />
              )}
              {currentView.pageId === "BILLING" && !hideBilling && (
                <BillingSettingsPage onExit={handleBack} />
              )}
              {currentView.pageId === "INTEGRATIONS" && (
                <IntegrationSettingsPage onExit={handleBack} />
              )}
              {currentView.pageId === "REFERRAL" && (
                <ReferralOnboarding onExit={handleBack} />
              )}
              {currentView.pageId === "SMART_INTAKE" && (
                <SmartIntakeSettingsPage onExit={handleBack} />
              )}
            </>
          )}

          {currentView.type === "onboarding-step" && (
            <>
              {currentView.stepId === "INTEGRATION" && (
                <IntegrationOnboarding
                  metadata={
                    steps.find((s) => s.id === "INTEGRATION")?.metadata as
                      IntegrationMetadata | undefined
                  }
                  onComplete={handleBack}
                />
              )}
              {currentView.stepId === "IMPORT_CLIENT" && (
                <ImportClientGuide
                  metadata={
                    steps.find((s) => s.id === "IMPORT_CLIENT")?.metadata as
                      ImportClientMetadata | undefined
                  }
                  onComplete={handleBack}
                />
              )}
              {currentView.stepId === "TEAM_MEMBERS" && (
                <TeamManagementOnboarding onExit={handleBack} />
              )}
              {currentView.stepId === "CUSTOMIZE_ONECLICK" && (
                <OneClickOnboarding onExit={handleBack} />
              )}
              {currentView.stepId === "REFERRAL" && (
                <ReferralOnboarding onExit={handleBack} />
              )}
            </>
          )}

          {currentView.type === "settings-grid" && (
            <SettingsCardsGrid
              onViewOnboardingSteps={handleToggleOnboardingSteps}
              isOnboardingCollapsed={currentView.type === "settings-grid"}
            />
          )}

          {currentView.type === "onboarding-grid" && (
            <OnboardingStepsGrid
              steps={steps}
              onStepClick={handleStepClick}
              loading={loading}
              error={error}
              loadingStepId={loadingStepId}
            />
          )}
        </Stack>
      </Container>
    </>
  );
};

export default SmartwizLiteHome;
